import { useContext, useEffect, useState } from "react"
import { AppWorkspaceContext } from "../stores/AppWorkspace"
import { agentDirectionsFor } from "../../docs/catalog"
import { loadAgentDirections, loadedAgentDirections, prefetchAgentDirections } from "../../docs/lazy"
import type { AgentStack } from "../../docs/types"
import {
  captureCopyAgentDirections,
  runAfterSuccessfulAction,
  type CopyCtaLocation
} from "../../analytics"

// The two things a visitor can put on their clipboard for a stack: the
// directions themselves — the payload the library generated, size-gated and
// link-checked, copied whole — or the absolute URL of the cover at
// /agent-directions/<stack>.md, for an agent that can fetch. `copied` names
// which button just fired so only it says "Copied" for a moment; the homepage
// modal, the framework cards and every landing page share this.
export type AgentDirectionsCopyKind = "directions" | "link"

export type AgentDirectionsCopied = { stack: AgentStack; kind: AgentDirectionsCopyKind }

// The directions' text is fetched per stack (docs/lazy). Once it has arrived a
// click copies it synchronously. Before that, Safari only honours a clipboard
// write that starts inside the click, so the write starts now with a
// ClipboardItem holding the text as a promise, filled in when the fetch lands.
// A browser without ClipboardItem writes the text after the wait instead.
const writeDirections = (stack: AgentStack): Promise<void> => {
  const ready = loadedAgentDirections(stack)
  if (ready != null) return navigator.clipboard.writeText(ready)
  const text = loadAgentDirections(stack)
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard.write) {
    return text.then((value) => navigator.clipboard.writeText(value))
  }
  const blob = text.then((value) => new Blob([value], { type: "text/plain" }))
  return navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })])
}

// Fetches a stack's directions once the page has settled, off the critical
// path, so the copy button on a page about that one stack never waits.
export const usePreloadAgentDirections = (stack: AgentStack, enabled = true) => {
  useEffect(() => {
    if (!enabled || stack === "start") return
    const run = () => prefetchAgentDirections(stack)
    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(run, { timeout: 4000 })
      return () => window.cancelIdleCallback(handle)
    }
    const timer = window.setTimeout(run, 1500)
    return () => window.clearTimeout(timer)
  }, [stack, enabled])
}

export const useCopyAgentDirections = (framework: string, ctaLocation: CopyCtaLocation) => {
  const workspace = useContext(AppWorkspaceContext)
  const [copied, setCopied] = useState<AgentDirectionsCopied | null>(null)

  const write = async (stack: AgentStack, kind: AgentDirectionsCopyKind, action: () => Promise<void>) => {
    const succeeded = await runAfterSuccessfulAction(action, () =>
      captureCopyAgentDirections({ framework, kind, ctaLocation })
    )
    if (succeeded) {
      setCopied({ stack, kind })
      window.setTimeout(() => setCopied(null), 1800)
    } else {
      setCopied(null)
    }
  }

  const copy = (stack: AgentStack) => write(stack, "directions", () => writeDirections(stack))

  const copyLink = (stack: AgentStack) => {
    const url = workspace.absoluteUrl(agentDirectionsFor(stack).coverPath)
    return write(stack, "link", () => navigator.clipboard.writeText(url))
  }

  const copiedKind = (stack: AgentStack): AgentDirectionsCopyKind | null =>
    copied?.stack === stack ? copied.kind : null

  return { copied, copiedKind, copy, copyLink }
}
